import {
  AdsClick,
  AutoAwesome,
  Balance,
  Bedtime,
  BubbleChart,
  CallSplit,
  CallMerge,
  Chat,
  Coffee,
  Commute,
  ConnectedTv,
  ContactMail,
  Dashboard,
  DepartureBoard,
  DirectionsBus,
  DirectionsCar,
  DirectionsRailway,
  DirectionsRun,
  DirectionsWalk,
  Event,
  Facebook,
  FactCheck,
  FolderOpen,
  Google,
  GpsFixed,
  GpsNotFixed,
  GpsOff,
  Group,
  Groups,
  Hotel,
  Interests,
  JoinInner,
  Mail,
  Map,
  Moving,
  Newspaper,
  NoTransfer,
  Numbers,
  People,
  Percent,
  Person,
  Place,
  Portrait,
  Public,
  Restaurant,
  School,
  Search,
  ShareLocation,
  ShoppingCart,
  Signpost,
  SocialDistance,
  Sort,
  SpeakerNotes,
  Today,
  Train,
  ViewArray,
  VolunteerActivism,
  WbTwilight,
  WbSunny,
  Weekend,
  WorkOutline,
  X,
} from "@mui/icons-material";
import { EyeotaLogo, MetaLogo } from "@/assets/partners";
import { PanelOption, SelectOption, TabOption } from "@/types";
import { toIndex, toPercentage } from "./format";

export const audienceSortOptions = [
  { name: "default", label: "Created Date" },
  { name: "name", label: "Audience Name" },
];

export const activationSortOptions = [
  { name: "default", label: "Created Date" },
  { name: "name", label: "Campaign Name" },
];
export const audienceFilterOptions = [
  { name: "build", label: "Build" },
  { name: "upload", label: "Upload" },
];

export const activationFilterOptions = [
  { name: "submitted", label: "Submitted" },
  { name: "in process", label: "In Process" },
  { name: "populated", label: "Populated" },
  { name: "ready", label: "Ready" },
  { name: "complete", label: "Complete" },
];

export const defaultSegment = { segment_id: 0, segment_name: "" };

export const operators = ["AND", "OR", "NOT", "SOME"];
export const operatorOptions: SelectOption[] = [
  { name: "AND", label: "All", description: "Match all of the variables"},
  { name: "OR", label: "Any", description: "Match any of the variables"},
  { name: "NOT", label: "None", description: "Match none of the variables"},
  { name: "SOME", label: "Some", description: "Match most of the variables, by taking an average score"},
]

export const groupOperators = ["AND", "OR"];
export const buildModes = ["broad", "balanced", "narrow"];
export const modeThreshold: { [key: string]: number } = {
  broad: 4,
  balanced: 6,
  narrow: 8,
};
export const scoreTypeMap: { [name: string]: string } = {
  score_wt: "Index",
  rate: "Penetration",
};

export const demographics = ["Age Band", "Income Band", "Lifestage"];

export const ageOptions = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
export const incomeOptions = ['Under £20k', '£20k-£30k', '£30k-£40k', '£40k-£50k', '£50k-£75k', '£75k-£100k', '£100k+'];

export const expandTypes = ['Spend Merchants']

export const buildModeOptions = [
  {
    value: "broad",
    label: "High Reach",
    description: "All over-indexing geographies",
    icon: <Groups />,
    iconComponent: Groups
  },
  {
    value: "balanced",
    label: "Balanced",
    description: "A balance of high scoring and high volume geographies",
    icon: <Balance />,
    iconComponent: Balance
  },
  {
    value: "narrow",
    label: "High Scoring",
    description: "The top scoring geographies for optimized targeting",
    icon: <AdsClick />,
    iconComponent: AdsClick
  }
];

export const locationModeOptions = [
  {
    value: "national",
    label: "National",
    description: "Nationwide targeting",
    icon: <ShareLocation />,
    iconComponent: ShareLocation
  },
  {
    value: "selected",
    label: "Select Location",
    description: "A balance of high scoring and high volume geographies",
    icon: <GpsFixed />,
    iconComponent: GpsFixed
  }
]

export const uploadModeOptions = [
  {
    value: "geo",
    label: "Geo",
    description:
      "Upload a file containing postcodes for pure geo-based insight and activation",
    icon: <Place />,
    iconComponent: Place,
    disabled: false,
  },
  {
    value: "customer",
    label: "Customer",
    description:
      "Upload your customer postcodes to understand weighted insights and enrich your customers with attributes",
    icon: <People />,
    iconComponent: People,
    disabled: false,
  },
  {
    value: "travel_time",
    label: "Traveltime",
    description:
      "Upload your site locations, define a travel time, and get insights about the postcodes surrounding your sites",
    icon: <DepartureBoard />,
    iconComponent: DepartureBoard,
    disabled: false,
    flag: 'Beta',
    flagDescription: 'This feature is currently in beta testing and may have limitations or bugs. Please report any issues you encounter.'
  },
];

export const travelTimeModeOptions = [
  {
    value: "combined",
    label: "Combined",
    description: "Combine the catchments into a single Audience to get holistic insights",
    icon: <Portrait />
  },
  {
    value: "segment",
    label: "Segment",
    description: "Treat each site/store location as a separate Segment to compare the insights",
    icon: <SocialDistance />
  },
]

export const travelTimeTransportOptions = [
  { name: "driving", label: "Driving", icon: <DirectionsCar fontSize="small"/> },
  { name: "walking", label: "Walking", icon: <DirectionsWalk fontSize="small"/>},
  { name: "public_transport", label: "Public Transport", icon: <DirectionsBus fontSize="small"/> },
];

export const travelTimeDepartureDayOptions = [
  {
    name: 'weekday',
    label: 'Weekday',
    icon: <WorkOutline fontSize="small"/>
  },
  {
    name: 'weekend',
    label: 'Weekend',
    icon: <Weekend fontSize="small"/>
  }
]

export const travelTimeDepartureTimeOptions = [
  {
    name: 'morning',
    label: 'Morning',
    icon: <WbTwilight fontSize="small"/>
  },
  {
    name: 'afternoon',
    label: 'Afternoon',
    icon: <WbSunny fontSize="small"/>
  },
  {
    name: 'evening',
    label: 'Evening',
    icon: <Bedtime fontSize="small"/>
  },
]

export const insightScoreOptions = [
  {
    value: "score_wt",
    label: "Index",
    icon: <Numbers />,
    description:
      "Index: The penetration value compared against the national average",
  },
  {
    value: "rate",
    label: "Penetration",
    icon: <Percent />,
    description:
      "Penetration: An estimate of the % of people we expect to have each variable",
  },
  // {
  //   value: "score_wt",
  //   label: "Weighted Index",
  //   icon: <Scale />
  // },
  {
    value: "score_rgn",
    label: "Region Index",
    icon: <Public />,
    description:
      "Regional Index: The penetration value compared against a regional average",
  },
];

export const insightLevelOptions = [
  {
    value: 3,
    label: "Local",
    description:
      "Local: These brands have a really small reach that tends not to extend beyond the local area",
    short: "Loc.",
    icon: <Person />,
  },
  {
    value: 2,
    label: "Regional",
    description:
      "Regional: These brands may be well known, but due to the nature of their business tend to still only attract customers who live nearby",
    short: "Reg.",
    icon: <Group />,
  },
  {
    value: 1,
    label: "National",
    description:
      "National: These will be the most well known and popular brands that have a nationwide presence",
    short: "Nat.",
    icon: <Groups />,
  },
];

export const insightSortOptions = [
  {
    value: "asc",
    label: "Asc.",
    icon: <Sort sx={{ transform: "scaleY(-1)" }} />,
    description: "Sort the data in ascending order based on the selected score",
  },
  {
    value: "desc",
    label: "Desc.",
    icon: <Sort />,
    description:
      "Sort the data in descending order based on the selected score",
  },
];

export const buildTabOptions: PanelOption[] = [
  {
    name: "directory",
    label: "Directory",
    icon: <FolderOpen fontSize="small" />,
    description:
      "Directory Lookup",
  },
  {
    name: "search",
    label: "Search",
    icon: <Search fontSize="small" />,
    description:
      "Global Search",
  },
  {
    name: "ai",
    label: "AI Search",
    icon: <AutoAwesome fontSize="small" />,
    description:
      "AI Search",
  }
]

export const insightTabOptions: TabOption[] = [
  {
    name: "pen_portrait",
    label: "Pen Portrait",
    icon: <Dashboard fontSize="small" />,
    permission: "",
    description:
      "A summary of your audience to give you a view of all of their behaviours in one place",
    showScores: true,
    showSorts: false,
    showLevels: true,
    showSearch: false,
    showDownload: false,
  },
  {
    name: "map",
    label: "Map",
    icon: <Map fontSize="small" />,
    permission: "map",
    description:
      "Visualise where your audience live and which geos are the best places to reach them",
    showScores: false,
    showLevels: false,
    showSorts: false,
    showSearch: false,
    showDownload: false,
  },
  {
    name: "demographics",
    label: "Demographics",
    icon: <People fontSize="small" />,
    permission: "data:demogs",
    description: "Understand the demographic makeup of your audience",
    showScores: true,
    showLevels: false,
    showSorts: false,
    showSearch: false,
    showDownload: true,
  },
  {
    name: "interests",
    label: "Interests",
    icon: <Interests fontSize="small" />,
    permission: "data:interests",
    description:
      "Analyse the interests of your audience to see what they love and care about",
    showScores: true,
    showLevels: false,
    showSorts: false,
    showSearch: false,
    showDownload: true,
  },
  {
    name: "spend",
    label: "Spend Patterns",
    icon: <ShoppingCart fontSize="small" />,
    permission: "data:snoop",
    description:
      "Understand where your audience is likely to spend their money",
    showScores: true,
    showLevels: true,
    showSorts: false,
    showSearch: true,
    showDownload: true,
  },
  {
    name: "mindsets",
    label: "Mindsets",
    icon: <BubbleChart fontSize="small" />,
    permission: "mindset",
    description: "Understand the WHY behind consumer behaviour through our Mindsets",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
    showDownload: true,
  },
  {
    name: "causes",
    label: "Causes",
    icon: <VolunteerActivism fontSize="small" />,
    permission: "data:causes",
    description:
      "See which charities and causes your audience are most likely to support",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
    showDownload: true,
  },
  {
    name: "media",
    label: "Media",
    icon: <Newspaper fontSize="small" />,
    permission: "data:tgi",
    client: ["gravity", "miroma", "demo"],
    description:
      "See which media channels your audience is most likely to resonate with",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
    showDownload: true,
  },
  {
    name: "attitudes",
    label: "Attitudes",
    icon: <FactCheck fontSize="small" />,
    permission: "data:tgi",
    client: ["gravity", "miroma", "demo"],
    description:
      "Analyse the behaviour of your audience along with their attitudes and preferences",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: true,
    showDownload: true,
  },
  {
    name: "digital",
    label: "Digital Consumption",
    icon: <ConnectedTv fontSize="small" />,
    permission: "data:tgi",
    client: ["gravity", "miroma", "demo"],
    description:
      "Understand how your audience use the internet and other digital products",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: true,
    showDownload: true,
  },
  {
    name: "statements",
    label: "Lifestyle Statements",
    icon: <SpeakerNotes fontSize="small" />,
    permission: "data:tgi",
    client: ["gravity", "miroma", "demo"],
    description:
      "See how your audience feel about different parts of their life",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: true,
    showDownload: true,
  },
  {
    name: "movement",
    label: "Movement",
    icon: <DirectionsRun fontSize="small" />,
    permission: "data:o2",
    client: ["core", "gravity", "demo"],
    description: "Show when and how your audience move around",
    showScores: true,
    showSorts: false,
    showLevels: false,
    showSearch: false,
    showDownload: true,
  },
  {
    name: "top scoring",
    label: "Top Scoring",
    icon: <Moving fontSize="small" />,
    permission: "",
    description: "",
    showScores: true,
    showSorts: true,
    showLevels: false,
    showSearch: false,
    showDownload: true,
  },
];

export const exportOptions = [
  {
    name: "postcode",
    label: "Postcode",
    type: "Geo",
    description: "The best Postcodes to reach your audience",
    icon: <Place />,
    allow_variables: true,
  },
  {
    name: "sector",
    label: "Sector",
    type: "Geo",
    description: "The best Postal Sectors to reach your audience",
    icon: <Place />,
    allow_variables: true,
  },
  {
    name: "door_drop",
    label: "Door Drop",
    type: "Mail",
    description: "Optimise your Door Drop campaigns using Postal Sectors",
    icon: <Mail />,
    allow_variables: false,
  },
  {
    name: "partially_addressed",
    label: "Partially Addressed",
    type: "Mail",
    description: "Target the right Postcodes for Partially Addressed Mail ",
    icon: <ContactMail />,
    allow_variables: false,
  },
  {
    name: "ooh",
    label: "OOH",
    type: "OOH",
    description: "Integrate with (D)OOH using Postcode Sectors",
    icon: <ViewArray />,
    allow_variables: false,
  },
  {
    name: "addressable_tv",
    label: "Addressable TV",
    type: "TV",
    description:
      "Reach the right households for your Addressable TV campaigns using Postal Sectors",
    icon: <ConnectedTv />,
    allow_variables: false,
  },
  {
    name: "geo_onboarding",
    label: "Geo Onboarding",
    type: "Digital",
    description:
      "Upload Postcodes into Digital Onboarding Environments (e.g. LiveRamp or Amazon Ads)",
    icon: <AdsClick />,
    allow_variables: false,
  },
  {
    name: "dv360",
    label: "DV360",
    type: "Digital",
    description: "Upload Latitude and Longitude pairs into DV360",
    icon: <Google />,
    allow_variables: false,
  },
  {
    name: "meta",
    label: "Meta",
    type: "Social",
    description: "Add granularity to your campaign in Meta Ads Manager",
    icon: <Facebook />,
    allow_variables: false,
  },
  {
    name: "twitter",
    label: "Twitter",
    type: "Social",
    description: "Optimise your Social campaign on X using Postal Sectors",
    icon: <X />,
    allow_variables: false,
  },
  {
    name: "lookalike",
    label: "Lookalikes",
    type: "Geo",
    description:
      "Create lookalikes from your Upload Audience to find postcodes that are similar",
    icon: <People />,
    allow_variables: false,
  },
  {
    name: "national",
    label: "National Export",
    type: "Geo",
    description:
      "A national view of where to find your audience, with composite scoring for downstream filtering",
    icon: <Public />,
    allow_variables: true,
    force_variables: true,
    permission: "export:full"
  },
];

export const campaignTypeOptions = [
  {
    value: "eyeota",
    label: "Programmatic",
    description:
      "Activate your prebuilt Starcount audiences within your favourite programmatic buying platforms",
    icon: <AdsClick />,
    disabled: false,
    message: `
      Proceeding will mean that your activation will get submitted to Eyeota and begin populating
      Please allow up to 7 days for it to be fully populated
      `,
  },
  {
    value: "meta",
    label: "Social",
    description:
      "Create recipes from your audiences using the established Meta platform taxonomy for social activations",
    icon: <Chat />,
    disabled: false,
    message: `
      Proceeding will mean that your activation will get submitted to Starcount and your Meta recipe will be created.
      It will then be your responsibility to copy this recipe into AdsManager to activate your audience.
     `,
  },
];

export const compareModeOptions = [
  {
    value: "Diff",
    label: "Diff",
    icon: <CallSplit />,
    description: "Highlight the variables that differ between audiences",
  },
  {
    value: "Overlap",
    label: "Overlap",
    icon: <CallMerge />,
    description: "Highlight the variables that both audiences have in common",
  },
]

export const toDemogStart = (v: string) => {
  switch (v) {
    case "Age Band":
      return "18-24";
    case "Income Band":
      return "£20k";
    case "Lifestage":
      return "Young";
    case "Social Grade":
      return "DE";
    case "House Price":
      return "£150k";
    case "Socio Economic Levels":
      return "10";
    case "Monthly Expenditure":
      return "£50";
    default:
      return "";
  }
};

export const toDemogEnd = (v: string) => {
  switch (v) {
    case "Age Band":
      return "65+";
    case "Income Band":
      return "£100k+";
    case "Lifestage":
      return "Retired";
    case "Social Grade":
      return "AB";
    case "House Price":
      return "£800k+";
    case "Socio Economic Levels":
      return "1";
    case "Monthly Expenditure":
      return "£500+";
    default:
      return "";
  }
};

export const activationPlatforms = {
  eyeota: [
    "Adform",
    "Adobe Ad Cloud (AAC)",
    "Adobe Audience Manager (AAM)",
    "Amazon (upon request)",
    "Aroscop (upon request)",
    "Basis",
    "BidTheatre",
    "Brand Network (Meta/Instagram, X, Pinterest & TikTok)",
    "DV360",
    "Epsilon Digital",
    "Equativ",
    "Google",
    "InMobi",
    "KARGO",
    "Knorex",
    "Lotame",
    "Magnite DV+",
    "MediaIQ (upon request)",
    "MGID (upon request)",
    "Neustar",
    "Nexxen (DSP & SSP)",
    "Outbrain SSP & Zemanta DSP",
    "PubMatic",
    "Roku OneView",
    "Sabio (upon request)",
    "SmartyAds",
    "StackAdapt",
    "Taboola",
    "The Trade Desk (TTD)",
    "Vistar Media",
    "Xandr (Apnnexus) & Curate (SSP)",
    "Yahoo!",
    "YOC",
    "Zeta (Sizmek)",
  ],
  meta: ["Meta"],
};

export const activationLogos = {
  eyeota: <EyeotaLogo />,
  meta: <MetaLogo />,
};

export const activationStatusDescription: { [key: string]: string } = {
  submitted: "The activation has been submitted to the Starcount team",
  "in process": "The data has been created and sent to Eyeota for processing",
  ready:
    "The activation segments have been created and are ready to be populated in the platform",
  populated:
    "The segments have been fully populated in the platform and are ready to buy against",
  complete: "The campaign dates have passed and the activation has expired",
};

export const movementOptions = [
  {
    name: "Time of Day",
    options: [
      {
        name: "Early Morning",
        label: "Early Morning (5am - 7am)",
        icon: <WbTwilight />,
      },
      {
        name: "Morning Commute",
        label: "Morning Commute (7am - 10am)",
        icon: <Train />,
      },
      {
        name: "Late Morning",
        label: "Late Morning (10am - 12pm)",
        icon: <Coffee />,
      },
      {
        name: "Lunchtime",
        label: "Lunchtime (12pm - 2pm)",
        icon: <Restaurant />,
      },
      { name: "Afternoon", label: "Afternoon (2pm - 5pm)", icon: <School /> },
      {
        name: "Evening Commute",
        label: "Evening Commute (5pm - 7pm)",
        icon: <Commute />,
      },
      { name: "Late Night", label: "Late Night (7pm - 12am)", icon: <Hotel /> },
      {
        name: "Night Owls",
        label: "Night Owls (12am - 5am)",
        icon: <Bedtime />,
      },
    ],
  },
  {
    name: "Transport Mode",
    options: [
      {
        name: "Walking or Cycling",
        label: "Walking or Cycling",
        icon: <DirectionsWalk />,
      },
      { name: "Rail", label: "Rail", icon: <DirectionsRailway /> },
      { name: "Driving", label: "Driving", icon: <DirectionsCar /> },
    ],
  },
  {
    name: "Commuters",
    options: [
      { name: "Commuters", label: "Commuters", icon: <DirectionsBus /> },
      { name: "Non-Commuters", label: "Non-Commuters", icon: <NoTransfer /> },
    ],
  },
];

export const genderOptions = [
  { name: "male", label: "Male" },
  { name: "female", label: "Female" },
  { name: "both", label: "Both" },
];

export const countryOptions = [
  { name: "GB", label: "United Kingdom" },
  { name: "US", label: "United States" },
];

export const penPortraitColumns = [
  { key: "tag_name", label: "Tag Name" },
  { key: "tag_category", label: "Tag Category" },
  { key: "tag_type", label: "Tag Type" },
  {
    key: "rate",
    label: "Penetration",
    formatter: (value: number) => toPercentage(value),
  },
  {
    key: "score_wt",
    label: "Score",
    formatter: (value: number) => String(toIndex(value)),
  },
  {
    key: "score_rgn",
    label: "Score Region",
    formatter: (value: number) => String(toIndex(value)),
  },
];

export const downloadColumns = [
  { name: "tag_name", label: "Tag Name" },
  { name: "tag_group", label: "Tag Group" },
  { name: "tag_category", label: "Tag Category" },
  { name: "tag_type", label: "Tag Type" },
  {
    name: "rate",
    label: "Penetration",
    formatter: (value: number) => toPercentage(value),
  },
  {
    name: "score_wt",
    label: "Score",
    formatter: (value: number) => String(toIndex(value)),
  },
  {
    name: "score_rgn",
    label: "Score Region",
    formatter: (value: number) => String(toIndex(value)),
  },
];


export const demoClients = [
  {
    name: 'bupa',
    label: 'Bupa'
  },
  {
    name: 'tfl',
    label: 'TfL / GLA / L&P'
  },
  {
    name: 'rubicon',
    label: 'Rubicon'
  },
  {
    name: 'bnq',
    label: 'B&Q'
  },
  {
    name: 'screwfix',
    label: 'Screwfix'
  },
  {
    name: 'bet365',
    label: 'Bet365'
  }
]
